// src/pages/auth/MagicLinkLogin.jsx
//
// Passwordless sign-in via emailed magic link. Sends a one-time link
// to the user's email; clicking it lands on /auth/link-callback which
// completes the sign-in. The email is stashed in localStorage under
// 'magicLinkEmail' so the callback can read it back (Firebase needs
// the same email used to send the link).
//
// Beta gate: refuses to send a link to a non-allowlisted email up
// front. Without this, the user would receive a working link, click
// it, sign in successfully, then get force-logged-out by AuthContext
// when the beta gate kicks in — way more confusing than a single
// "you're not on the list" toast.
'use client';

import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { auth } from '@/firebase/firebase';
import toast from 'react-hot-toast';
import { isEmailAllowed, BETA_GATE_MESSAGE } from '@/context/AuthContext';
import AuthShell, {
  AuthPill,
  AuthHeading,
  AuthSubtitle,
  AuthButton,
  AuthBanner,
  authInputClass,
  authLabelClass,
} from '@/components/auth/AuthShell';

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const actionCodeSettings = {
  url: `${window.location.origin}/auth/link-callback`,
  handleCodeInApp: true,
};

export default function MagicLinkLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [sent, setSent] = useState(false);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = emailValid && !loading;

  const handleSendLink = async (e) => {
    e?.preventDefault();
    setBannerError('');

    if (!email.trim()) {
      setBannerError('Enter your email to continue.');
      return;
    }
    if (!emailValid) {
      setBannerError('Please enter a valid email address.');
      return;
    }
    if (!isEmailAllowed(email)) {
      // During private beta we refuse to send a link the recipient
      // wouldn't be able to use. AuthContext would otherwise
      // force-log-out the user immediately after sign-in succeeds —
      // confusing for everyone. Better to fail loud here.
      setBannerError(BETA_GATE_MESSAGE);
      return;
    }

    setLoading(true);
    try {
      await sendSignInLinkToEmail(auth, email.trim(), actionCodeSettings);
      window.localStorage.setItem('magicLinkEmail', email.trim());
      setSent(true);
      toast.success('Login link sent! Check your inbox.');
    } catch (err) {
      console.error('[MagicLinkLogin] send error', err);
      const msg = err?.message || 'Failed to send the sign-in link. Try again.';
      setBannerError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="text-center">
        <AuthPill>{sent ? 'Check your inbox' : 'Sign in via email'}</AuthPill>

        {sent ? (
          <AuthHeading>
            Link <span className="text-[#b5f03d]">sent.</span>
          </AuthHeading>
        ) : (
          <AuthHeading>
            Sign in <span className="text-[#b5f03d]">without</span>
            <br />
            a password.
          </AuthHeading>
        )}

        <AuthSubtitle>
          {sent
            ? `We sent a sign-in link to ${email.trim()}. Open it on the same device and you'll be signed in.`
            : 'Enter your email and we will send you a one-tap sign-in link. No password required.'}
        </AuthSubtitle>
      </div>

      <div className="mt-7">
        <AuthBanner
          tone="error"
          title={bannerError ? 'Could not send link' : null}
          onClose={bannerError ? () => setBannerError('') : undefined}
        >
          {bannerError}
        </AuthBanner>

        {!sent ? (
          <form onSubmit={handleSendLink} className="space-y-4 text-left" noValidate>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="email"
                className={authLabelClass}
                style={{ fontFamily: HEAD_FONT }}
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (bannerError) setBannerError('');
                }}
                required
                autoFocus
                autoComplete="email"
                placeholder="coach@yourteam.com"
                aria-invalid={!!bannerError || (email.length > 0 && !emailValid)}
                disabled={loading}
                className={authInputClass}
              />
              {email.length > 0 && !emailValid && !bannerError && (
                <p className="mt-1 text-xs text-red-300">
                  Please enter a valid email address.
                </p>
              )}
            </div>

            <AuthButton
              type="submit"
              disabled={!canSubmit}
              busy={loading}
              className="mt-2"
            >
              {loading ? 'Sending link…' : 'Send sign-in link'}
            </AuthButton>
          </form>
        ) : (
          <AuthButton
            type="button"
            onClick={() => {
              setSent(false);
              setEmail('');
            }}
            className="mt-2"
          >
            Send to a different email
          </AuthButton>
        )}

        <p className="mt-6 text-center text-sm text-white/55">
          Prefer a password?{' '}
          <RouterLink
            to="/login"
            className="font-semibold text-[#b5f03d] hover:underline"
          >
            Sign in with password
          </RouterLink>
        </p>
        <p className="mt-2 text-center text-xs text-white/40">
          New to Scrimly?{' '}
          <RouterLink
            to="/register"
            className="font-semibold text-[#b5f03d]/80 hover:text-[#b5f03d] hover:underline"
          >
            Create a free account
          </RouterLink>
        </p>
      </div>
    </AuthShell>
  );
}
